import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CardContent, Grid, Typography } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import {
  createStyles,
  makeStyles,
  Theme,
  useTheme,
} from '@material-ui/core/styles';
import { CancelToken } from 'axios';
import moment from 'moment';
import { Bar } from 'react-chartjs-2';
import { useSelector } from 'react-redux';
import ApartmentIcon from '@material-ui/icons/Apartment';
import Card from '../../../../components/Card';
import {
  ChartData,
  StackedBarDataset,
} from '../../../../models/dto/ExpectedRealizedDTO';
import {
  getPunchCardAttributeLabel,
  PunchCardDetailsChartDTO,
  PunchCardGroupedChartDTO,
} from '../../../../models/dto/PunchCardDetailsDTO';
import { getCancelTokenSource, isCancel } from '../../../../services/api';
import { getPunchCardPeriodChartData } from '../../../../services/personnelDepartment.service';
import { AppState } from '../../../../store';
import { CondominiumReducerData } from '../../../../store/reducers/condominiumReducer';
import {
  startOf,
  subtract,
  toDefaultServerDateFormat,
} from '../../../../utils/date.util';
import { isEmpty } from '../../../../utils/validation.util';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    cardHeaderContainer: {
      padding: theme.spacing(2),
      paddingBottom: 0,
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: window.innerHeight * 0.23,
    },
    contentContainer: {
      paddingTop: 0,
    },
  }),
);

type PunchCardDetailsChartType = Omit<
  PunchCardGroupedChartDTO,
  'mesReferencia'
>;

const puchCardGroupedProperties: string[] = [
  'horasTrabalhadas',
  'horasExtras',
  'horasAfastamento',
  'horasAtrasos',
  'horasFaltas',
  'horasFerias',
];

const PunchCardDetailsCard: React.FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState<ChartData | null>(null);

  const { condominiumSelected }: CondominiumReducerData = useSelector(
    (state: AppState) => state.condominiumReducer,
  );

  const mappedAttributeLabel = Object.freeze({
    horasTrabalhadas: {
      stack: 'stack1',
      backgroundColor: theme.palette.success.main,
      hoverBackgroundColor: theme.palette.success.light,
      data: [],
    },
    horasFaltas: {
      stack: 'stack1',
      backgroundColor: theme.palette.error.main,
      hoverBackgroundColor: theme.palette.error.light,
      data: [],
    },
    horasAtrasos: {
      stack: 'stack1',
      backgroundColor: theme.palette.warning.main,
      hoverBackgroundColor: theme.palette.warning.light,
      data: [],
    },
    horasExtras: {
      stack: 'stack1',
      backgroundColor: theme.palette.info.main,
      hoverBackgroundColor: theme.palette.info.light,
      data: [],
    },
    horasAfastamento: {
      stack: 'stack1',
      backgroundColor: theme.palette.grey[400],
      hoverBackgroundColor: theme.palette.grey[300],
      data: [],
    },
    horasFerias: {
      stack: 'stack1',
      backgroundColor: theme.palette.secondary.main,
      hoverBackgroundColor: theme.palette.secondary.light,
      data: [],
    },
  });

  const buildPunchCardDetailsChartDataset = useCallback(
    (data: PunchCardDetailsChartDTO[]) => {
      const labels: string[] = [];
      const datasets: StackedBarDataset[] = [];

      data.forEach(item => {
        labels.push(moment(item.mesReferencia, 'DD/MM/YYYY').format('MMMM/YY'));
        puchCardGroupedProperties.forEach(prop => {
          const key = prop as keyof PunchCardDetailsChartType;
          const label = getPunchCardAttributeLabel(key);
          const numberValue = item[key];

          if (numberValue) {
            (mappedAttributeLabel[key].data as number[]).push(numberValue);
          }

          let existentIndex = datasets.findIndex(item => item.label === label);

          if (existentIndex === -1) {
            datasets.push({ ...mappedAttributeLabel[key], label });
          }
        });
      });

      const builtChartData: ChartData = { labels, datasets };

      setChartData(builtChartData);
    },
    [mappedAttributeLabel],
  );

  const findPunchCardDetailData = useCallback(
    async (cancelToken?: CancelToken) => {
      try {
        setLoading(true);
        const today = moment().toDate();
        const aYearAgo = toDefaultServerDateFormat(
          startOf(subtract(today, 2, 'month'), 'month'),
        );
        const currentDay = toDefaultServerDateFormat(today);
        const reference = condominiumSelected?.referencia;
        if (!isEmpty(aYearAgo) && !isEmpty(currentDay) && reference) {
          const { data } = await getPunchCardPeriodChartData(
            aYearAgo,
            currentDay,
            reference,
            cancelToken,
          );
          buildPunchCardDetailsChartDataset(data);
        }
      } catch (error) {
        if (!isCancel(error)) {
          console.log(error);
        }
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [condominiumSelected],
  );

  useEffect(() => {
    const cancelTokenSource = getCancelTokenSource();
    findPunchCardDetailData(cancelTokenSource.token);
  }, [findPunchCardDetailData]);

  const punchCardDetailsChart = (
    <Bar
      data={chartData || {}}
      height={window.innerHeight * 0.135}
      options={{
        legend: {
          display: true,
          align: 'start',
          scales: {
            xAxes: [
              {
                stacked: true,
              },
            ],
            yAxes: [
              {
                stacked: true,
              },
            ],
          },
        },
        tooltips: {
          mode: 'label',
          callbacks: {
            label(tooltipItem: any, data: any) {
              return `${data.datasets[tooltipItem.datasetIndex].label}: ${
                tooltipItem.yLabel
              }`;
            },
          },
        },
      }}
    />
  );

  const mainElement = useMemo(() => {
    let element = (
      <Grid container direction="column" className={classes.loadingContainer}>
        <ApartmentIcon style={{ fontSize: 70, opacity: 0.4 }} />
        <Typography color="textSecondary" variant="body1">
          Selecione um condomínio para exibir este gráfico
        </Typography>
      </Grid>
    );

    if (condominiumSelected) {
      if (loading) {
        element = (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        );
      } else {
        element = punchCardDetailsChart;
      }
    }

    return element;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condominiumSelected, loading, punchCardDetailsChart]);

  return (
    <Card>
      <Grid
        container
        alignItems="center"
        justify="space-between"
        className={classes.cardHeaderContainer}
      >
        <Grid item>
          <Typography color="textSecondary" variant="h5">
            Ocorrências - Visão Geral
          </Typography>
        </Grid>
      </Grid>

      <CardContent className={classes.contentContainer}>
        {mainElement}
      </CardContent>
    </Card>
  );
};

export default PunchCardDetailsCard;
